import React, { useState } from 'react';
import {
  Box, Flex, Heading, Text, Button, Input, Textarea, Select,
  FormControl, FormLabel, FormHelperText, SimpleGrid,
  Card, CardBody, VStack, HStack, useColorModeValue, useToast,
  Tag, Wrap, WrapItem, IconButton, Progress
} from '@chakra-ui/react';
import {
  ArrowLeft, Upload, X, Plus, Brain, Save
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Dashboard/Sidebar';
import { uploadModel } from '../api/modelService';

// Default feature extraction for audio models; must match how the model was trained.
const DEFAULT_AUDIO_INFERENCE_CONFIG = JSON.stringify({
  task: 'audio-mfcc-classification',
  sample_rate: 22050,
  duration_sec: 30,
  n_mfcc: 40,
  aggregation: 'mean',
}, null, 2);

// The hub runs this model itself (inference-service), so no model file is needed for the demo
const AYAH_IDENTIFICATION_CONFIG = JSON.stringify({ task: 'quran-ayah-identification' }, null, 2);
const RECITATION_CHECK_CONFIG = JSON.stringify({ task: 'quran-recitation-check', expected_ayah: '1:1' }, null, 2);
// The hub computes Whisper encoder features and the uploaded .skops model classifies them
const WHISPER_EMBEDDING_CONFIG = JSON.stringify({ task: 'whisper-embedding-classification', duration_sec: 30 }, null, 2);
// The hub computes DINOv2 image features and the uploaded .skops model classifies them
const IMAGE_EMBEDDING_CONFIG = JSON.stringify({ task: 'image-embedding-classification' }, null, 2);

const MODEL_FILE_ACCEPT = '.skops,.onnx,.safetensors,.pkl,.joblib,.zip';

const ModelUpload = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Form state
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: '',
    framework: '',
    input_type: '',
    output_type: '',
    accuracy: '',
    layers_info: '',
    model_size_mb: '',
    training_time_sec: '',
    num_classes: '',
    num_samples: '',
    demo_instructions: '',
    sample_input_description: '',
    metrics: '',
    inference_config: '',
  });
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [modelFile, setModelFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);

  const cardBg = useColorModeValue('white', 'gray.800');
  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const dropHoverBg = useColorModeValue('teal.50', 'teal.900');

  const categories = ['Classification', 'NLP', 'Time-Series', 'Audio', 'Recommender', 'Computer Vision', 'Regression'];
  const frameworks = ['scikit-learn', 'PyTorch', 'TensorFlow', 'AutoGluon', 'Keras', 'ONNX', 'statsmodels', 'Other'];
  const inputTypes = ['image', 'audio', 'text', 'tabular'];

  const handleChange = (field, value) => {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      // Pre-fill the demo feature settings when an audio or image model is chosen
      if (field === 'input_type' && value === 'audio' && (!prev.inference_config || prev.inference_config === IMAGE_EMBEDDING_CONFIG)) {
        next.inference_config = DEFAULT_AUDIO_INFERENCE_CONFIG;
      }
      if (field === 'input_type' && value === 'image') {
        next.inference_config = IMAGE_EMBEDDING_CONFIG;
      }
      return next;
    });
  };

  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setTags(tags.filter(t => t !== tag));
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!form.title || !form.category || !form.framework || !form.input_type) {
      toast({ title: 'Missing required fields', description: 'Please fill in title, category, framework, and input type.', status: 'warning' });
      return;
    }

    if (form.inference_config) {
      try {
        JSON.parse(form.inference_config);
      } catch {
        toast({ title: 'Invalid demo settings', description: 'Inference config must be valid JSON.', status: 'warning' });
        return;
      }
    }

    setSubmitting(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('title', form.title);
      formData.append('description', form.description);
      formData.append('category', form.category);
      formData.append('framework', form.framework);
      formData.append('input_type', form.input_type);
      formData.append('output_type', form.output_type);
      
      if (form.accuracy) formData.append('accuracy', parseFloat(form.accuracy) / 100); // Convert % to decimal
      if (form.layers_info) formData.append('layers_info', form.layers_info);
      if (form.model_size_mb) formData.append('model_size_mb', form.model_size_mb);
      if (form.training_time_sec) formData.append('training_time_sec', form.training_time_sec);
      if (form.num_classes) formData.append('num_classes', form.num_classes);
      if (form.num_samples) formData.append('num_samples', form.num_samples);
      if (form.demo_instructions) formData.append('demo_instructions', form.demo_instructions);
      if (form.sample_input_description) formData.append('sample_input_description', form.sample_input_description);
      if (tags.length > 0) formData.append('tags', tags.join(','));
      if (form.metrics) formData.append('metrics', form.metrics);
      if (form.inference_config) formData.append('inference_config', form.inference_config);

      if (modelFile) formData.append('model_file', modelFile);
      if (thumbnailFile) formData.append('thumbnail', thumbnailFile);

      const token = localStorage.getItem('token');
      const result = await uploadModel(formData, token, setUploadProgress);

      toast({
        title: 'Model submitted',
        description: 'It will be visible to others once an admin approves it.',
        status: 'success',
        duration: 5000,
      });
      navigate(`/models/${result.model.id}`);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast({ title: 'Upload failed', description: msg, status: 'error', duration: 5000 });
    }
    setSubmitting(false);
    setUploadProgress(0);
  };

  return (
    <Flex direction="row" minH="100vh" bg={pageBg}>
      <Sidebar isCollapsed={sidebarCollapsed} toggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)} />

      <Box
        pl={sidebarCollapsed ? "60px" : "250px"}
        w="full" overflowY="auto" pb={8}
        transition="padding-left 0.2s ease"
      >
        {/* Header */}
        <Box px={8} py={6} borderBottom="1px" borderColor={borderColor} bg={cardBg}>
          <HStack mb={4}>
            <Button variant="ghost" leftIcon={<ArrowLeft size={18} />}
              onClick={() => navigate('/models')} size="sm">
              Back to Models
            </Button>
          </HStack>
          <HStack>
            <Upload size={28} color="var(--chakra-colors-teal-500)" />
            <Heading size="lg">Upload New Model</Heading>
          </HStack>
          <Text color="gray.500" mt={1}>
            Add a trained machine learning model with metadata and optional model file.
            New models stay pending until an admin reviews them.
          </Text>
        </Box>

        <Box px={8} py={6} maxW="900px">
          {/* Upload Progress */}
          {submitting && (
            <Progress value={uploadProgress} size="sm" colorScheme="teal" mb={6} borderRadius="full" />
          )}

          <VStack spacing={6} align="stretch">
            {/* Basic Info */}
            <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
              <CardBody>
                <Heading size="sm" mb={4}>📋 Basic Information</Heading>
                <VStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>Model Title</FormLabel>
                    <Input placeholder="e.g. Quran Audio Reciter Classifier"
                      value={form.title} onChange={e => handleChange('title', e.target.value)} />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Description</FormLabel>
                    <Textarea placeholder="Describe what this model does, how it was trained, and its purpose..."
                      rows={4} value={form.description}
                      onChange={e => handleChange('description', e.target.value)} />
                  </FormControl>

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} w="full">
                    <FormControl isRequired>
                      <FormLabel>Category</FormLabel>
                      <Select placeholder="Select category" value={form.category}
                        onChange={e => handleChange('category', e.target.value)}>
                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                      </Select>
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>Framework</FormLabel>
                      <Select placeholder="Select framework" value={form.framework}
                        onChange={e => handleChange('framework', e.target.value)}>
                        {frameworks.map(f => <option key={f} value={f}>{f}</option>)}
                      </Select>
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>Input Type</FormLabel>
                      <Select placeholder="Select input type" value={form.input_type}
                        onChange={e => handleChange('input_type', e.target.value)}>
                        {inputTypes.map(t => <option key={t} value={t}>{t}</option>)}
                      </Select>
                    </FormControl>
                  </SimpleGrid>

                  <FormControl>
                    <FormLabel>Output Type</FormLabel>
                    <Input placeholder="e.g. class_label, probability, regression_value"
                      value={form.output_type} onChange={e => handleChange('output_type', e.target.value)} />
                  </FormControl>
                </VStack>
              </CardBody>
            </Card>

            {/* Performance Metrics */}
            <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
              <CardBody>
                <Heading size="sm" mb={4}>📊 Performance Metrics</Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel>Accuracy (%)</FormLabel>
                    <Input type="number" min="0" max="100" step="0.1"
                      placeholder="e.g. 98.5" value={form.accuracy}
                      onChange={e => handleChange('accuracy', e.target.value)} />
                    <FormHelperText>Primary accuracy metric (0-100%)</FormHelperText>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Training Time (seconds)</FormLabel>
                    <Input type="number" step="0.01"
                      placeholder="e.g. 41.92" value={form.training_time_sec}
                      onChange={e => handleChange('training_time_sec', e.target.value)} />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Number of Classes</FormLabel>
                    <Input type="number" min="1"
                      placeholder="e.g. 6" value={form.num_classes}
                      onChange={e => handleChange('num_classes', e.target.value)} />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Number of Training Samples</FormLabel>
                    <Input type="number" min="1"
                      placeholder="e.g. 344" value={form.num_samples}
                      onChange={e => handleChange('num_samples', e.target.value)} />
                  </FormControl>
                </SimpleGrid>

                <FormControl mt={4}>
                  <FormLabel>Additional Metrics (JSON)</FormLabel>
                  <Textarea placeholder='{"MAE": 2218.5, "RMSE": 2787.75, "MAPE": 15.8}'
                    rows={3} fontFamily="mono" fontSize="sm"
                    value={form.metrics} onChange={e => handleChange('metrics', e.target.value)} />
                  <FormHelperText>JSON object with additional metrics</FormHelperText>
                </FormControl>
              </CardBody>
            </Card>

            {/* Architecture & Technical Details */}
            <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
              <CardBody>
                <Heading size="sm" mb={4}>🏗️ Architecture & Technical Details</Heading>
                <VStack spacing={4}>
                  <FormControl>
                    <FormLabel>Model Architecture / Layers Info</FormLabel>
                    <Textarea
                      placeholder="e.g. Conv2d(3,16) → ReLU → MaxPool → Conv2d(16,32) → ReLU → MaxPool → FC(128) → FC(num_classes)"
                      rows={4} fontFamily="mono" fontSize="sm"
                      value={form.layers_info} onChange={e => handleChange('layers_info', e.target.value)} />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Model Size (MB)</FormLabel>
                    <Input type="number" step="0.01"
                      placeholder="Auto-calculated if file uploaded" value={form.model_size_mb}
                      onChange={e => handleChange('model_size_mb', e.target.value)} />
                  </FormControl>
                </VStack>
              </CardBody>
            </Card>

            {/* Demo Configuration */}
            <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
              <CardBody>
                <Heading size="sm" mb={4}>🎮 Demo Configuration</Heading>
                <VStack spacing={4}>
                  <FormControl>
                    <FormLabel>Demo Instructions</FormLabel>
                    <Textarea
                      placeholder="e.g. Upload an MP3 file of Quran recitation (max 30 seconds). The model will predict the reciter."
                      rows={3} value={form.demo_instructions}
                      onChange={e => handleChange('demo_instructions', e.target.value)} />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Expected Input Description</FormLabel>
                    <Input
                      placeholder="e.g. MP3 audio file, 128x128 PNG image, CSV file with headers"
                      value={form.sample_input_description}
                      onChange={e => handleChange('sample_input_description', e.target.value)} />
                  </FormControl>

                  {form.input_type === 'audio' && (
                    <FormControl>
                      <FormLabel>Audio Feature Settings (inference config)</FormLabel>
                      <Textarea rows={7} fontFamily="mono" fontSize="sm"
                        value={form.inference_config}
                        onChange={e => handleChange('inference_config', e.target.value)} />
                      <HStack mt={2}>
                        <Button size="xs" variant="outline"
                          onClick={() => handleChange('inference_config', DEFAULT_AUDIO_INFERENCE_CONFIG)}>
                          MFCC classifier (.skops)
                        </Button>
                        <Button size="xs" variant="outline"
                          onClick={() => handleChange('inference_config', WHISPER_EMBEDDING_CONFIG)}>
                          Whisper-feature classifier (.skops)
                        </Button>
                        <Button size="xs" variant="outline"
                          onClick={() => handleChange('inference_config', AYAH_IDENTIFICATION_CONFIG)}>
                          Built-in Surah/Ayah identifier
                        </Button>
                        <Button size="xs" variant="outline"
                          onClick={() => handleChange('inference_config', RECITATION_CHECK_CONFIG)}>
                          Built-in recitation checker
                        </Button>
                      </HStack>
                      <FormHelperText>
                        How the live demo runs the model. For a .skops classifier, use the same sample rate,
                        duration and number of MFCCs the model was trained with. A Whisper-feature classifier
                        must be trained on ml-models/common/whisper_embedding.py features. The recitation
                        checker compares a recitation with expected_ayah (surah:ayah) word by word.
                      </FormHelperText>
                    </FormControl>
                  )}

                  {form.input_type === 'image' && (
                    <FormControl>
                      <FormLabel>Image Feature Settings (inference config)</FormLabel>
                      <Textarea rows={3} fontFamily="mono" fontSize="sm"
                        value={form.inference_config}
                        onChange={e => handleChange('inference_config', e.target.value)} />
                      <FormHelperText>
                        For the live demo, upload a .skops classifier trained on the DINOv2 image features from
                        ml-models/common/image_embedding.py; the hub computes the same features for each uploaded image.
                        Clear this box if the model has no demo.
                      </FormHelperText>
                    </FormControl>
                  )}
                </VStack>
              </CardBody>
            </Card>

            {/* Tags */}
            <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
              <CardBody>
                <Heading size="sm" mb={4}>🏷️ Tags</Heading>
                <HStack mb={3}>
                  <Input placeholder="Add a tag" value={tagInput}
                    onChange={e => setTagInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddTag())} />
                  <IconButton icon={<Plus size={16} />} onClick={handleAddTag} colorScheme="teal" />
                </HStack>
                <Wrap spacing={2}>
                  {tags.map((tag, i) => (
                    <WrapItem key={i}>
                      <Tag size="md" colorScheme="teal" borderRadius="full">
                        {tag}
                        <IconButton icon={<X size={12} />} size="xs" variant="ghost"
                          ml={1} onClick={() => handleRemoveTag(tag)} />
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              </CardBody>
            </Card>

            {/* File Uploads */}
            <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
              <CardBody>
                <Heading size="sm" mb={4}>📁 File Uploads</Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel>Model File</FormLabel>
                    <Box border="2px dashed" borderColor={borderColor} borderRadius="xl"
                      p={6} textAlign="center" cursor="pointer" position="relative"
                      _hover={{ borderColor: 'teal.400', bg: dropHoverBg }}
                      transition="all 0.2s">
                      <Input type="file" position="absolute" top={0} left={0} w="full" h="full"
                        opacity={0} cursor="pointer"
                        accept={MODEL_FILE_ACCEPT}
                        onChange={e => setModelFile(e.target.files[0])} />
                      <Brain size={32} color="var(--chakra-colors-teal-400)" style={{ margin: '0 auto 8px' }} />
                      <Text fontSize="sm" fontWeight="medium">
                        {modelFile ? modelFile.name : 'Click to upload model file'}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {modelFile ? `${(modelFile.size / 1024 / 1024).toFixed(2)} MB` : '.skops, .onnx, .safetensors, .pkl, .joblib, .zip (max 500 MB)'}
                      </Text>
                    </Box>
                    <FormHelperText>
                      The live demo runs scikit-learn models saved with skops (.skops). Other formats
                      are stored for download only.
                    </FormHelperText>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Thumbnail Image</FormLabel>
                    <Box border="2px dashed" borderColor={borderColor} borderRadius="xl"
                      p={6} textAlign="center" cursor="pointer" position="relative"
                      _hover={{ borderColor: 'teal.400', bg: dropHoverBg }}
                      transition="all 0.2s">
                      <Input type="file" position="absolute" top={0} left={0} w="full" h="full"
                        opacity={0} cursor="pointer" accept="image/*"
                        onChange={e => setThumbnailFile(e.target.files[0])} />
                      <Upload size={32} color="var(--chakra-colors-teal-400)" style={{ margin: '0 auto 8px' }} />
                      <Text fontSize="sm" fontWeight="medium">
                        {thumbnailFile ? thumbnailFile.name : 'Click to upload thumbnail'}
                      </Text>
                      <Text fontSize="xs" color="gray.500">PNG, JPG, or WebP</Text>
                    </Box>
                  </FormControl>
                </SimpleGrid>
              </CardBody>
            </Card>

            {/* Submit */}
            <Flex justify="flex-end" gap={3}>
              <Button variant="outline" onClick={() => navigate('/models')} size="lg">
                Cancel
              </Button>
              <Button
                leftIcon={<Save size={18} />}
                colorScheme="teal"
                size="lg"
                borderRadius="xl"
                onClick={handleSubmit}
                isLoading={submitting}
                loadingText="Uploading..."
              >
                Upload Model
              </Button>
            </Flex>
          </VStack>
        </Box>
      </Box>
    </Flex>
  );
};

export default ModelUpload;
